import type {
  ChatFeatures,
  LoginResponse,
  MeResponse,
  TokenLimitStatus,
} from '@nycelogic/nycebuddy-contracts';
import {
  createBuddyClient,
  createTokenSession,
  webStore,
  type BuddyClient,
} from '@nycelogic/nycebuddy-api-client';

export const BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api/buddy';
export const CLIENT_VERSION: string = typeof __BUILD_HASH__ !== 'undefined' ? __BUILD_HASH__ : 'dev';

const sessionStore = webStore(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);
const localStore = webStore(typeof localStorage !== 'undefined' ? localStorage : undefined);

const K = {
  system: 'nb:system',
  features: 'nb:features',
  systemName: 'nb:system_name',
  tenantName: 'nb:tenant_name',
  organizationName: 'nb:organization_name',
  username: 'nb:username',
  userCode: 'nb:user_code',
  language: 'nb:language',
  lastSystem: 'nb:last_system',
  theme: 'nb:ui_theme',
  fontSize: 'nb:ui_font_size',
  debug: 'nb:debug',
};

export interface LoginInfo {
  system: string;
  systemName?: string;
  tenantName?: string;
  organizationName?: string;
  username: string;
  /** Sign-in name (`user.code`), for flows that ask for the password again. */
  userCode?: string;
  features: Partial<ChatFeatures>;
  language?: string;
  limitStatus?: TokenLimitStatus;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let limitCache: TokenLimitStatus | null = null;
let snapshot: LoginInfo | null = readLogin();

function readLogin(): LoginInfo | null {
  const system = sessionStore.get(K.system);
  if (!system || !sessionStore.get('nb:token')) return null;
  let features: Partial<ChatFeatures>;
  try {
    features = JSON.parse(sessionStore.get(K.features) || '{}');
  } catch {
    features = {};
  }
  return {
    system,
    systemName: sessionStore.get(K.systemName) ?? undefined,
    tenantName: sessionStore.get(K.tenantName) ?? undefined,
    organizationName: sessionStore.get(K.organizationName) ?? undefined,
    username: sessionStore.get(K.username) ?? '',
    userCode: sessionStore.get(K.userCode) ?? undefined,
    features,
    language: sessionStore.get(K.language) ?? undefined,
    limitStatus: limitCache ?? undefined,
  };
}

function notify() {
  snapshot = readLogin();
  listeners.forEach((l) => l());
}

/** Access + refresh tokens in sessionStorage (`nb:token`, `nb:refresh`, ...), refreshed at 60 % of their lifetime. */
export const session = createTokenSession({
  baseUrl: BASE_URL,
  store: sessionStore,
  onExpired: () => {
    clearLogin();
  },
});

export const client: BuddyClient = createBuddyClient({
  baseUrl: BASE_URL,
  auth: session,
  system: sessionStore.get(K.system) ?? undefined,
  clientVersion: CLIENT_VERSION,
});

export function storeLogin(res: LoginResponse): LoginInfo {
  session.setTokens(res);
  limitCache = res.limitStatus ?? null;
  sessionStore.set(K.system, res.system);
  sessionStore.set(K.features, JSON.stringify(res.features ?? {}));
  if (res.systemName) sessionStore.set(K.systemName, res.systemName);
  if (res.tenantName) sessionStore.set(K.tenantName, res.tenantName);
  if (res.organizationName) sessionStore.set(K.organizationName, res.organizationName);
  sessionStore.set(K.username, res.user?.name || res.user?.code || '');
  if (res.user?.code) sessionStore.set(K.userCode, res.user.code);
  if (res.language) sessionStore.set(K.language, res.language);
  localStore.set(K.lastSystem, res.system);
  client.setSystem(res.system);
  notify();
  return snapshot!;
}

export function updateFromMe(me: MeResponse): void {
  if (me.limitStatus) limitCache = me.limitStatus;
  if (me.system) sessionStore.set(K.system, me.system);
  if (me.systemName) sessionStore.set(K.systemName, me.systemName);
  if (me.tenantName) sessionStore.set(K.tenantName, me.tenantName);
  if (me.organizationName) sessionStore.set(K.organizationName, me.organizationName);
  if (me.user) sessionStore.set(K.username, me.user.name || me.user.code);
  if (me.user?.code) sessionStore.set(K.userCode, me.user.code);
  if (me.features) sessionStore.set(K.features, JSON.stringify(me.features));
  if (me.language) sessionStore.set(K.language, me.language);
  notify();
}

export function clearLogin(): void {
  session.clear();
  limitCache = null;
  for (const k of [
    K.system,
    K.features,
    K.systemName,
    K.tenantName,
    K.organizationName,
    K.username,
    K.userCode,
    K.language,
  ])
    sessionStore.remove(k);
  notify();
}

export async function logout(): Promise<void> {
  const rt = session.refreshToken();
  try {
    if (session.peek()) await client.logout(rt ?? undefined);
  } catch {
    /* best effort */
  }
  clearLogin();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const getLogin = (): LoginInfo | null => snapshot;

export const prefs = {
  lastSystem: () => localStore.get(K.lastSystem),
  theme: (): 'dark' | 'light' | 'auto' => (localStore.get(K.theme) as 'dark' | 'light' | 'auto') || 'dark',
  setTheme: (t: string) => localStore.set(K.theme, t),
  fontSize: (): 'small' | 'medium' | 'large' =>
    (localStore.get(K.fontSize) as 'small' | 'medium' | 'large') || 'medium',
  debug: (): boolean => {
    const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
    for (const [k, v] of params)
      if (k.toLowerCase() === 'debugmode' || k.toLowerCase() === 'debug')
        return v.toLowerCase() === 'true' || v === '1';
    return localStore.get(K.debug) === 'true';
  },
};
